import type { Icon } from '../HugeIcon/huge-icon';

export type EvaluationTaskType =
  | 'openQuestion'
  | 'multipleChoice'
  | 'gapText'
  | 'matching'
  | 'chatbot'
  | 'fileUpload';

export type EvaluationIconState =
  | 'correct'
  | 'partiallyCorrect'
  | 'incorrect'
  | 'inProgress'
  | 'submitted'
  | 'notAnswered';

export type EvaluationIconSize = 'sm' | 'md' | 'lg';

export type EvaluationIconProps = {
  state: EvaluationIconState;
  size?: EvaluationIconSize;
  ariaLabel?: string;
  tooltip?: string;
};

export type EvaluationTaskTypeIconProps = {
  taskType: EvaluationTaskType;
  number?: number;
  size?: EvaluationIconSize;
  ariaLabel?: string;
  tooltip?: string;
  tooltipPlacement?: 'top' | 'bottom';
  state?: EvaluationIconState;
};

export type EvaluationIconStateContent = {
  kind: 'icon';
  icon: Icon;
};

export const evaluationTaskTypeIcons: Record<EvaluationTaskType, Icon> = {
  openQuestion: 'question_mark_square',
  multipleChoice: 'checklist',
  gapText: 'text_field',
  matching: 'arrow_left_right',
  chatbot: 'bot',
  fileUpload: 'file',
};

export const evaluationIconStateStyles: Record<
  EvaluationIconState,
  { container: string; interactiveContainer: string }
> = {
  correct: {
    container: 'border border-success-100 bg-success-50 text-success-500',
    interactiveContainer: 'transition-colors hover:bg-success-100',
  },
  partiallyCorrect: {
    container: 'border border-warning-100 bg-warning-50 text-warning-500',
    interactiveContainer: 'transition-colors hover:bg-warning-100',
  },
  incorrect: {
    container: 'border border-danger-100 bg-danger-50 text-danger-500',
    interactiveContainer: 'transition-colors hover:bg-danger-100',
  },
  inProgress: {
    container: 'border border-info-100 bg-info-50 text-info-500',
    interactiveContainer: 'transition-colors hover:bg-info-100',
  },
  submitted: {
    container: 'border border-info-100 bg-info-50 text-info-500',
    interactiveContainer: 'transition-colors hover:bg-info-100',
  },
  notAnswered: {
    container: 'border border-neutral-200 bg-neutral-50 text-neutral-500',
    interactiveContainer: 'transition-colors hover:bg-neutral-100',
  },
};

export const evaluationIconStateContent: Record<
  Exclude<EvaluationIconState, 'notAnswered'>,
  EvaluationIconStateContent
> = {
  correct: { kind: 'icon', icon: 'checkmark' },
  partiallyCorrect: { kind: 'icon', icon: 'solid_line' },
  incorrect: { kind: 'icon', icon: 'cancel' },
  inProgress: { kind: 'icon', icon: 'edit' },
  submitted: { kind: 'icon', icon: 'file' },
};

export const evaluationTaskTypeIconStyles = {
  container: 'bg-neutral-50 text-neutral-800',
  numberBadgeText: 'text-neutral-600',
} as const;

export const evaluationIconDimensions: Record<
  EvaluationIconSize,
  {
    container: string;
    iconSize: number;
    taskTypeIconWrapper: string;
    numberBadge: string;
    numberBadgePosition: string;
  }
> = {
  sm: {
    container: 'size-6',
    iconSize: 14,
    taskTypeIconWrapper: 'p-0.5',
    numberBadge: 'h-3.5 w-3.5 text-[0.5rem]',
    numberBadgePosition: '-bottom-0.5 -right-1',
  },
  md: {
    container: 'size-8',
    iconSize: 18,
    taskTypeIconWrapper: 'p-1',
    numberBadge: 'h-4 w-4 text-[0.625rem]',
    numberBadgePosition: '-bottom-1 -right-1',
  },
  lg: {
    container: 'size-10',
    iconSize: 22,
    taskTypeIconWrapper: 'p-1.5',
    numberBadge: 'h-5 w-5 text-xs',
    numberBadgePosition: '-bottom-1 -right-1',
  },
};
